// dxfImport.ts — R2000 DXF reader for cv-web.
//
// Pairs with dxfExport.ts: parses the grammar that exporter emits, plus a
// best-effort mapping from cv-cad's lowercase layer names. Returns one or
// more PanelData with warnings[]; partial import is always allowed.
//
// Recognized entities: LINE (grouped into rectangles), LWPOLYLINE (outline
// or rectangle), CIRCLE, TEXT, MTEXT. DIMENSION is read for layer/position
// only and discarded (cv-web recomputes). 3DSOLID is skipped with a
// warning — the proper fix is the cv-cad-side `c:cvexport-2d` companion
// (CV-NNN).

import type { PanelData, MpVar, Slot } from '../types/panelvar';
import { defaultPanelData, mergePanelDefaults } from '../types/panelvar';

interface Vertex { x: number; y: number; }

// ── DXF tokenization ────────────────────────────────────────────────────────

interface Pair { code: number; value: string; }

function tokenize(text: string): Pair[] {
  // DXF is line-pair: odd lines = group code (integer), even = value.
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const pairs: Pair[] = [];
  for (let i = 0; i + 1 < lines.length; i += 2) {
    const codeStr = lines[i].trim();
    const value = lines[i + 1];
    if (codeStr === '') continue;
    const code = parseInt(codeStr, 10);
    if (Number.isNaN(code)) continue;
    pairs.push({ code, value });
  }
  return pairs;
}

// ── Entity model ────────────────────────────────────────────────────────────

interface BaseEntity { type: string; layer: string; }
interface LineEnt   extends BaseEntity { type: 'LINE'; x1: number; y1: number; x2: number; y2: number; }
interface LwPolyEnt extends BaseEntity { type: 'LWPOLYLINE'; vertices: Vertex[]; closed: boolean; }
interface CircleEnt extends BaseEntity { type: 'CIRCLE'; cx: number; cy: number; r: number; }
interface TextEnt   extends BaseEntity { type: 'TEXT' | 'MTEXT'; x: number; y: number; value: string; height: number; }
interface DimEnt    extends BaseEntity { type: 'DIMENSION'; }
interface SolidEnt  extends BaseEntity { type: '3DSOLID'; }
interface InsertEnt extends BaseEntity { type: 'INSERT'; block: string; x: number; y: number; }
interface AttribEnt extends BaseEntity { type: 'ATTRIB'; tag: string; value: string; x: number; y: number; }

type Entity = LineEnt | LwPolyEnt | CircleEnt | TextEnt | DimEnt | SolidEnt | InsertEnt | AttribEnt;

function parseEntities(pairs: Pair[]): Entity[] {
  const entities: Entity[] = [];
  let inEntities = false;
  let i = 0;

  // Walk to ENTITIES section
  while (i < pairs.length) {
    if (pairs[i].code === 0 && pairs[i].value === 'SECTION') {
      if (pairs[i + 1] && pairs[i + 1].code === 2 && pairs[i + 1].value === 'ENTITIES') {
        inEntities = true;
        i += 2;
        break;
      }
    }
    i++;
  }
  if (!inEntities) return entities;

  // Each entity starts with code 0 + type, runs to next code 0
  while (i < pairs.length) {
    if (pairs[i].code !== 0) { i++; continue; }
    const type = pairs[i].value;
    if (type === 'ENDSEC') break;
    i++;

    // Collect pairs until next code 0
    const fields = new Map<number, string[]>();
    while (i < pairs.length && pairs[i].code !== 0) {
      const list = fields.get(pairs[i].code) ?? [];
      list.push(pairs[i].value);
      fields.set(pairs[i].code, list);
      i++;
    }

    const layer = (fields.get(8)?.[0] ?? '0').trim();
    const num = (code: number, idx = 0): number => {
      const v = fields.get(code)?.[idx];
      return v ? parseFloat(v) : NaN;
    };

    if (type === 'LINE') {
      entities.push({ type: 'LINE', layer, x1: num(10), y1: num(20), x2: num(11), y2: num(21) });
    } else if (type === 'LWPOLYLINE') {
      const xs = fields.get(10) ?? [];
      const ys = fields.get(20) ?? [];
      const flag = parseInt(fields.get(70)?.[0] ?? '0', 10);
      const vertices: Vertex[] = [];
      for (let k = 0; k < Math.min(xs.length, ys.length); k++) {
        vertices.push({ x: parseFloat(xs[k]), y: parseFloat(ys[k]) });
      }
      entities.push({ type: 'LWPOLYLINE', layer, vertices, closed: (flag & 1) === 1 });
    } else if (type === 'CIRCLE') {
      entities.push({ type: 'CIRCLE', layer, cx: num(10), cy: num(20), r: num(40) });
    } else if (type === 'TEXT') {
      entities.push({
        type: 'TEXT', layer,
        x: num(10), y: num(20),
        value: fields.get(1)?.[0] ?? '',
        height: num(40),
      });
    } else if (type === 'MTEXT') {
      entities.push({
        type: 'MTEXT', layer,
        x: num(10), y: num(20),
        value: (fields.get(1) ?? []).concat(fields.get(3) ?? []).join(''),
        height: num(40),
      });
    } else if (type === 'DIMENSION') {
      entities.push({ type: 'DIMENSION', layer });
    } else if (type === '3DSOLID' || type === 'ACAD_PROXY_ENTITY' || type === 'BODY') {
      entities.push({ type: '3DSOLID', layer });
    } else if (type === 'INSERT') {
      entities.push({
        type: 'INSERT', layer,
        block: (fields.get(2)?.[0] ?? '').trim(),
        x: num(10), y: num(20),
      });
    } else if (type === 'ATTRIB') {
      entities.push({
        type: 'ATTRIB', layer,
        tag: (fields.get(2)?.[0] ?? '').trim(),
        value: fields.get(1)?.[0] ?? '',
        x: num(10), y: num(20),
      });
    }
    // Other types (POINT, SOLID, HATCH, SEQEND, LEADER, …) ignored for v1.
  }

  return entities;
}

// ── Layer normalization ─────────────────────────────────────────────────────

const LAYER_MAP: Record<string, string> = {
  // cv-web canonical (uppercase) — passthrough
  PANEL:       'PANEL',
  OPENINGS:    'OPENINGS',
  BLOCKOUTS:   'BLOCKOUTS',
  FEATURES:    'FEATURES',
  STRUCTURAL:  'STRUCTURAL',
  DIMENSIONS:  'DIMENSIONS',
  NOTES:       'NOTES',
  REVISIONS:   'REVISIONS',
  SITE:        'SITE',
  GRID:        'GRID',
  // cv-cad lowercase → cv-web uppercase
  PERIMETER:     'PANEL',
  PERIMETER_DIM: 'DIMENSIONS',
  SOLID:         'PANEL',
  SOLID_DIM:     'DIMENSIONS',
  FEATURE:       'FEATURES',
  FEATURE_DIM:   'DIMENSIONS',
  CONNECTIONS:   'STRUCTURAL',
  POINTS:        'STRUCTURAL',
  GREENPLATE:    'STRUCTURAL',
};

function normalizeLayer(raw: string): string {
  const up = raw.trim().toUpperCase();
  return LAYER_MAP[up] ?? up;
}

// ── Rectangle detection from 4 LINEs ────────────────────────────────────────

interface Rect { x: number; y: number; w: number; h: number; layer: string; }

const EPS = 0.01; // inches — endpoint match tolerance

interface NormLine {
  origIdx: number;
  horiz:   boolean;  // true if y1 == y2
  vert:    boolean;
  xmin:    number;
  xmax:    number;
  ymin:    number;
  ymax:    number;
}

function normalize(line: LineEnt, idx: number): NormLine | null {
  const horiz = Math.abs(line.y1 - line.y2) < EPS;
  const vert  = Math.abs(line.x1 - line.x2) < EPS;
  if (!horiz && !vert) return null;  // diagonals not part of rect detection
  return {
    origIdx: idx,
    horiz, vert,
    xmin: Math.min(line.x1, line.x2),
    xmax: Math.max(line.x1, line.x2),
    ymin: Math.min(line.y1, line.y2),
    ymax: Math.max(line.y1, line.y2),
  };
}

function rectsFromLines(lines: LineEnt[]): { rects: Rect[]; unmatched: LineEnt[] } {
  const rects: Rect[] = [];
  const unmatched: LineEnt[] = [];

  const byLayer = new Map<string, LineEnt[]>();
  for (const l of lines) {
    const list = byLayer.get(l.layer) ?? [];
    list.push(l);
    byLayer.set(l.layer, list);
  }

  for (const [layer, list] of byLayer) {
    const norms: NormLine[] = [];
    const diagonals: number[] = [];
    for (let i = 0; i < list.length; i++) {
      const n = normalize(list[i], i);
      if (n) norms.push(n);
      else   diagonals.push(i);
    }

    const horizes = norms.filter(n => n.horiz);
    const verts   = norms.filter(n => n.vert);
    const usedHoriz = new Set<number>();
    const usedVert  = new Set<number>();

    // Find rectangles: every pair (h1, h2) of horizontal lines that share
    // the same x-extent must be paired with two vertical lines connecting
    // them at xmin and xmax. The 4 then form a rectangle.
    for (let i = 0; i < horizes.length; i++) {
      const h1 = horizes[i];
      if (usedHoriz.has(h1.origIdx)) continue;
      for (let j = i + 1; j < horizes.length; j++) {
        const h2 = horizes[j];
        if (usedHoriz.has(h2.origIdx)) continue;
        if (Math.abs(h1.xmin - h2.xmin) > EPS) continue;
        if (Math.abs(h1.xmax - h2.xmax) > EPS) continue;
        if (Math.abs(h1.ymin - h2.ymin) < EPS) continue;  // same line

        const ymin = Math.min(h1.ymin, h2.ymin);
        const ymax = Math.max(h1.ymin, h2.ymin);
        const xmin = h1.xmin;
        const xmax = h1.xmax;

        // Find left + right vertical lines
        const left  = verts.find(v =>
          !usedVert.has(v.origIdx) &&
          Math.abs(v.xmin - xmin) < EPS &&
          Math.abs(v.ymin - ymin) < EPS &&
          Math.abs(v.ymax - ymax) < EPS);
        if (!left) continue;
        const right = verts.find(v =>
          !usedVert.has(v.origIdx) &&
          Math.abs(v.xmin - xmax) < EPS &&
          Math.abs(v.ymin - ymin) < EPS &&
          Math.abs(v.ymax - ymax) < EPS);
        if (!right) continue;

        usedHoriz.add(h1.origIdx);
        usedHoriz.add(h2.origIdx);
        usedVert.add(left.origIdx);
        usedVert.add(right.origIdx);
        rects.push({ x: xmin, y: ymin, w: xmax - xmin, h: ymax - ymin, layer });
        break;
      }
    }

    for (let i = 0; i < list.length; i++) {
      const isHoriz = Math.abs(list[i].y1 - list[i].y2) < EPS;
      if (isHoriz && usedHoriz.has(i)) continue;
      if (!isHoriz && usedVert.has(i)) continue;
      if (diagonals.includes(i)) { unmatched.push(list[i]); continue; }
      // Only report as unmatched if it's a horiz/vert line that didn't pair
      if (!usedHoriz.has(i) && !usedVert.has(i)) unmatched.push(list[i]);
    }
  }

  return { rects, unmatched };
}

function rectFromLwPoly(p: LwPolyEnt): Rect | null {
  if (p.vertices.length !== 4 || !p.closed) return null;
  const xs = p.vertices.map(v => v.x);
  const ys = p.vertices.map(v => v.y);
  const xmin = Math.min(...xs), xmax = Math.max(...xs);
  const ymin = Math.min(...ys), ymax = Math.max(...ys);
  // Each vertex must lie on a rect corner
  for (const v of p.vertices) {
    const onX = Math.abs(v.x - xmin) < EPS || Math.abs(v.x - xmax) < EPS;
    const onY = Math.abs(v.y - ymin) < EPS || Math.abs(v.y - ymax) < EPS;
    if (!onX || !onY) return null;
  }
  return { x: xmin, y: ymin, w: xmax - xmin, h: ymax - ymin, layer: p.layer };
}

// ── cv-cad ATTRIB / INSERT vocabulary ──────────────────────────────────────
// The shop drawing cv-cad emits encodes panel data three ways simultaneously:
//   1. solid_dim ATTRIBs (tags W/H/E/…) = literal mpvar dump
//   2. layer-0 title-block ATTRIBs (tags MPX1/FILE/MPPN/…) = metadata
//   3. INSERTs on connections/points layers with WC*/PP*/BP* block names
// The 3DSOLIDs on the `solid` layer are visually redundant with this data —
// every field a carpenter reads is in the ATTRIBs above.

/** "7 1/4\"", "5'-10 3/8\"", "-1'-2 1/2\"" → inches as float. */
function parseFeetInches(s: string): number | null {
  const cleaned = s.replace(/"/g, '').trim();
  if (cleaned === '') return null;
  const m = cleaned.match(/^(-?)(?:(\d+)'-?\s*)?(\d+)?(?:\s+(\d+)\/(\d+))?$/);
  if (!m) {
    const f = parseFloat(cleaned);
    return Number.isNaN(f) ? null : f;
  }
  const sign = m[1] === '-' ? -1 : 1;
  const feet = m[2] ? parseInt(m[2], 10) : 0;
  const inch = m[3] ? parseInt(m[3], 10) : 0;
  const num  = m[4] ? parseInt(m[4], 10) : 0;
  const den  = m[5] ? parseInt(m[5], 10) : 1;
  return sign * (feet * 12 + inch + (den ? num / den : 0));
}

// cv-cad title block ATTRIB tags on layer 0
const TITLE_ATTRIB_MAP: Record<string, keyof MpVar> = {
  FILE: 'panelMark',     // CSB001 — also written to drawingNumber
  MPPN: 'projectName',
  MPDB: 'drawBy',
  DATE: 'date',
};

// cv-cad solid_dim mpvar-dump ATTRIB tags
const MPDUMP_NUMERIC: Record<string, keyof MpVar> = {
  W: 'width',
  H: 'height',
};

function decodeCvCadAttribs(mp: MpVar, attribs: AttribEnt[]): { found: boolean; metadata: Record<string, string> } {
  let found = false;
  const metadata: Record<string, string> = {};

  for (const a of attribs) {
    const tag = a.tag.toUpperCase();
    if (!tag) continue;

    if (tag in TITLE_ATTRIB_MAP) {
      const key = TITLE_ATTRIB_MAP[tag];
      (mp as unknown as Record<string, string>)[key] = a.value.trim();
      if (tag === 'FILE') mp.drawingNumber = a.value.trim();
      found = true;
    } else if (tag === 'MPX1') {
      const t = parseFeetInches(a.value);
      if (t !== null) mp.thickness = t;
      found = true;
    } else if (tag in MPDUMP_NUMERIC) {
      const n = parseFloat(a.value);
      if (!Number.isNaN(n)) {
        (mp as unknown as Record<string, number>)[MPDUMP_NUMERIC[tag]] = n;
        found = true;
      }
    } else if (a.value && a.value.trim()) {
      metadata[tag] = a.value.trim();
    }
  }
  return { found, metadata };
}

function decodeCvCadInserts(panel: PanelData, inserts: InsertEnt[], warnings: string[]) {
  for (const ins of inserts) {
    const block = ins.block.trim();
    if (!block) continue;
    const pfx2 = block.slice(0, 2).toUpperCase();
    const pfx3 = block.slice(0, 3).toUpperCase();

    if (pfx2 === 'WC') {
      const slot = nextSlot(panel.wcvar);
      if (!slot) { warnings.push('Weld connections: out of slots — extra entry skipped.'); continue; }
      slot.enabled = true;
      slot.data.x = ins.x;
      slot.data.y = ins.y;
      slot.data.mark = block;
    } else if (pfx2 === 'PP') {
      const slot = nextSlot(panel.ppvar);
      if (!slot) { warnings.push('Pick points: out of slots — extra entry skipped.'); continue; }
      slot.enabled = true;
      slot.data.x = ins.x;
      slot.data.y = ins.y;
    } else if (pfx2 === 'BP') {
      const slot = nextSlot(panel.bpvar);
      if (!slot) { warnings.push('Brace points: out of slots — extra entry skipped.'); continue; }
      slot.enabled = true;
      slot.data.x = ins.x;
      slot.data.y = ins.y;
    }
    // TITLE / ELEV / NUM and pfx3-prefixed dim blocks → drawing decoration, ignored
    void pfx3;
  }
}

// ── Title-block parsing ─────────────────────────────────────────────────────

const TB_FIELD_PATTERNS: Array<{ key: keyof MpVar; prefix: string }> = [
  { key: 'projectName',   prefix: 'PROJECT:' },
  { key: 'drawingNumber', prefix: 'DRAWING:' },
  { key: 'panelMark',     prefix: 'MARK:' },
  { key: 'date',          prefix: 'DATE:' },
  { key: 'drawBy',        prefix: 'DWN:' },
  { key: 'checkBy',       prefix: 'CHK:' },
  { key: 'revision',      prefix: 'REV:' },
];

function applyTitleBlock(mp: MpVar, texts: TextEnt[]): TextEnt[] {
  const remaining: TextEnt[] = [];
  for (const t of texts) {
    let matched = false;
    for (const { key, prefix } of TB_FIELD_PATTERNS) {
      if (t.value.startsWith(prefix)) {
        const val = t.value.slice(prefix.length).trim();
        (mp as unknown as Record<string, string>)[key as string] = val;
        matched = true;
        break;
      }
    }
    if (!matched && t.value.startsWith('THKNS:')) {
      const num = parseFloat(t.value.slice(6).replace(/[^\d.\-]/g, ''));
      if (!Number.isNaN(num)) mp.thickness = num;
      matched = true;
    }
    if (!matched) remaining.push(t);
  }
  return remaining;
}

// ── Slot helpers ────────────────────────────────────────────────────────────

function nextSlot<T>(slots: Slot<T>[]): Slot<T> | null {
  for (const s of slots) if (!s.enabled) return s;
  return null;
}

// ── Main import ─────────────────────────────────────────────────────────────

export interface DXFImportResult {
  panels:   PanelData[];
  warnings: string[];
}

/** Parse a DXF document and reconstruct a PanelData. Throws only on
 *  unrecoverable input (empty file). Partial matches return warnings[]. */
export function importDXFString(text: string): DXFImportResult {
  const warnings: string[] = [];

  if (!text || text.length < 8) {
    return { panels: [], warnings: ['DXF file is empty.'] };
  }

  const pairs = tokenize(text);
  const ents  = parseEntities(pairs);

  if (ents.length === 0) {
    return { panels: [], warnings: ['No entities found in DXF (no ENTITIES section or all unrecognized types).'] };
  }

  // Preserve raw layer before normalization (cv-cad ATTRIB routing is by tag,
  // not by layer — and the normalized layer would alias solid_dim → DIMENSIONS).
  for (const e of ents) e.layer = normalizeLayer(e.layer);

  // Bucket by entity type
  const lines     = ents.filter(e => e.type === 'LINE')       as LineEnt[];
  const lwpolys   = ents.filter(e => e.type === 'LWPOLYLINE') as LwPolyEnt[];
  const circles   = ents.filter(e => e.type === 'CIRCLE')     as CircleEnt[];
  const texts     = ents.filter(e => e.type === 'TEXT' || e.type === 'MTEXT') as TextEnt[];
  const inserts   = ents.filter(e => e.type === 'INSERT')     as InsertEnt[];
  const attribs   = ents.filter(e => e.type === 'ATTRIB')     as AttribEnt[];

  // cv-cad signal: a populated ATTRIB block stamps the panel data right into
  // the drawing. If present, the 3DSOLID/LINE warnings are noise (those are
  // visual mass + dim-leader lines, all redundant with the ATTRIB dump).
  const cvCadAttribsPresent = attribs.some(a => {
    const t = a.tag.toUpperCase();
    return t === 'W' || t === 'H' || t === 'MPX1' || t === 'FILE' || t === 'MPPN';
  });

  const solidCount = ents.filter(e => e.type === '3DSOLID').length;
  if (solidCount > 0 && !cvCadAttribsPresent) {
    warnings.push(
      `${solidCount} 3DSOLID entit${solidCount === 1 ? 'y' : 'ies'} found and skipped. ` +
      `Re-export from cv-cad as a 2D companion DXF (planned: c:cvexport-2d).`,
    );
  }

  // Pull rectangles from LINEs and 4-vertex LWPOLYLINEs
  const { rects: lineRects, unmatched } = rectsFromLines(lines);
  const polyRects: Rect[] = [];
  const outlinePolys: LwPolyEnt[] = [];
  for (const p of lwpolys) {
    const r = rectFromLwPoly(p);
    if (r) polyRects.push(r);
    else if (p.layer === 'PANEL' && p.closed) outlinePolys.push(p);
    else if (p.layer === 'PANEL') outlinePolys.push(p); // open polygon — still attempt
  }
  const allRects = [...lineRects, ...polyRects];

  if (unmatched.length > 0 && !cvCadAttribsPresent) {
    warnings.push(`${unmatched.length} unmatched LINE entit${unmatched.length === 1 ? 'y' : 'ies'} (could not group into rectangles).`);
  }

  // ── Build the panel ───────────────────────────────────────────────────────
  const panel = defaultPanelData(crypto.randomUUID());
  const mp = panel.mpvar;

  // cv-cad ATTRIB decode runs first — it's the most authoritative source.
  let cvCadMetadata: Record<string, string> = {};
  if (cvCadAttribsPresent) {
    const r = decodeCvCadAttribs(mp, attribs);
    cvCadMetadata = r.metadata;
    decodeCvCadInserts(panel, inserts, warnings);
  }

  // Title block fields take precedence over geometric inference
  const notesTexts = texts.filter(t => t.layer === 'NOTES');
  const otherTexts = texts.filter(t => t.layer !== 'NOTES');
  const remainingNotes = applyTitleBlock(mp, notesTexts);

  // Drop NOTES rectangles (title block frame + dividers)
  const panelRects = allRects.filter(r => r.layer !== 'NOTES');

  // ── Outline → mpvar.width / height ────────────────────────────────────────
  const panelOutlineRects = panelRects.filter(r => r.layer === 'PANEL');
  if (outlinePolys.length > 0) {
    // Use the largest outline polygon
    const poly = outlinePolys.reduce((best, p) =>
      (bboxArea(p.vertices) > bboxArea(best.vertices) ? p : best), outlinePolys[0]);
    const bb = polyBBox(poly.vertices);
    mp.width  = bb.maxX - bb.minX;
    mp.height = bb.maxY - bb.minY;
    // Detect simple gable / trapezoid shapes: 5+ vertices and a peak above bbox-midline
    if (poly.vertices.length > 4) {
      warnings.push(
        `Panel outline has ${poly.vertices.length} vertices — irregular top shape detected; ` +
        `width × height bbox preserved, top-angle/radius reset to defaults.`,
      );
    }
  } else if (panelOutlineRects.length > 0) {
    const r = panelOutlineRects.reduce((best, c) => (c.w * c.h > best.w * best.h ? c : best), panelOutlineRects[0]);
    mp.width  = r.w;
    mp.height = r.h;
  } else if (!cvCadAttribsPresent) {
    warnings.push('No PANEL outline found — width/height left at defaults.');
  }
  // When cvCadAttribsPresent, mp.width/height came from the W/H ATTRIBs above.
  void cvCadMetadata;

  // ── Openings ─────────────────────────────────────────────────────────────
  const openingRects = panelRects.filter(r => r.layer === 'OPENINGS');
  for (const r of openingRects) {
    const tag = nearestText(r, otherTexts.concat(remainingNotes));
    const type = classifyOpening(tag?.value ?? '');
    placeOpening(panel, r, tag?.value ?? '', type, warnings);
  }

  // ── Blockouts ────────────────────────────────────────────────────────────
  const blockoutRects = panelRects.filter(r => r.layer === 'BLOCKOUTS');
  for (const r of blockoutRects) {
    const slot = nextSlot(panel.sbvar);
    if (!slot) { warnings.push('Rectangular blockouts: out of slots — extra entry skipped.'); continue; }
    slot.enabled = true;
    slot.data.x = r.x + r.w / 2;
    slot.data.y = r.y + r.h / 2;
    slot.data.width  = r.w;
    slot.data.height = r.h;
    const t = nearestText(r, notesTexts);
    if (t) slot.data.mark = t.value;
  }
  const blockoutCircles = circles.filter(c => c.layer === 'BLOCKOUTS');
  for (const c of blockoutCircles) {
    const slot = nextSlot(panel.rbvar);
    if (!slot) { warnings.push('Round blockouts: out of slots — extra entry skipped.'); continue; }
    slot.enabled = true;
    slot.data.x = c.cx;
    slot.data.y = c.cy;
    slot.data.diameter = c.r * 2;
    const t = nearestText({ x: c.cx - c.r, y: c.cy - c.r, w: c.r * 2, h: c.r * 2, layer: 'BLOCKOUTS' }, notesTexts);
    if (t) slot.data.mark = t.value;
  }

  // ── Feature strips ───────────────────────────────────────────────────────
  const featureRects = panelRects.filter(r => r.layer === 'FEATURES');
  for (const r of featureRects) {
    if (r.w >= r.h) {
      const slot = nextSlot(panel.fhvar);
      if (!slot) { warnings.push('Horizontal features: out of slots — extra entry skipped.'); continue; }
      slot.enabled = true;
      slot.data.y      = r.y;
      slot.data.height = r.h;
      const fullWidth = mp.width > 0 && Math.abs(r.w - mp.width) < EPS && r.x < EPS;
      if (fullWidth) {
        slot.data.xStart = 0;
        slot.data.xEnd   = 0;
        slot.data.width  = mp.width;
      } else {
        slot.data.xStart = r.x;
        slot.data.xEnd   = r.x + r.w;
        slot.data.width  = r.w;
      }
    } else {
      const slot = nextSlot(panel.fvvar);
      if (!slot) { warnings.push('Vertical features: out of slots — extra entry skipped.'); continue; }
      slot.enabled = true;
      slot.data.x      = r.x + r.w / 2;
      slot.data.width  = r.w;
      const fullHeight = mp.height > 0 && Math.abs(r.h - mp.height) < EPS && r.y < EPS;
      if (fullHeight) {
        slot.data.yStart = 0;
        slot.data.yEnd   = 0;
      } else {
        slot.data.yStart = r.y;
        slot.data.yEnd   = r.y + r.h;
      }
    }
  }

  // ── Structural circles → brace / pick ────────────────────────────────────
  const structCircles = circles.filter(c => c.layer === 'STRUCTURAL');
  for (const c of structCircles) {
    // Brace = r 3.0 (dxfExport line 171); Pick = r 4.0 (line 178).
    // Threshold midway is 3.5".
    const isPick = c.r >= 3.5;
    if (isPick) {
      const slot = nextSlot(panel.ppvar);
      if (!slot) { warnings.push('Pick points: out of slots — extra entry skipped.'); continue; }
      slot.enabled = true;
      slot.data.x = c.cx;
      slot.data.y = c.cy;
    } else {
      const slot = nextSlot(panel.bpvar);
      if (!slot) { warnings.push('Brace points: out of slots — extra entry skipped.'); continue; }
      slot.enabled = true;
      slot.data.x = c.cx;
      slot.data.y = c.cy;
    }
  }

  // ── Done ─────────────────────────────────────────────────────────────────
  return { panels: [mergePanelDefaults(panel)], warnings };
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function polyBBox(verts: Vertex[]): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const v of verts) {
    if (v.x < minX) minX = v.x; if (v.x > maxX) maxX = v.x;
    if (v.y < minY) minY = v.y; if (v.y > maxY) maxY = v.y;
  }
  return { minX, minY, maxX, maxY };
}
function bboxArea(verts: Vertex[]): number {
  const b = polyBBox(verts);
  return (b.maxX - b.minX) * (b.maxY - b.minY);
}

function nearestText(rect: Rect, texts: TextEnt[]): TextEnt | null {
  // First preference: text whose insertion point lies inside the rect
  for (const t of texts) {
    if (t.x >= rect.x - EPS && t.x <= rect.x + rect.w + EPS &&
        t.y >= rect.y - EPS && t.y <= rect.y + rect.h + EPS) {
      return t;
    }
  }
  // Otherwise: closest text to rect center
  const cx = rect.x + rect.w / 2;
  const cy = rect.y + rect.h / 2;
  let best: TextEnt | null = null;
  let bestD = Infinity;
  for (const t of texts) {
    const d = (t.x - cx) ** 2 + (t.y - cy) ** 2;
    if (d < bestD) { bestD = d; best = t; }
  }
  // Only accept if "close enough" — within ~2× the rect's longer side
  if (best && bestD < Math.max(rect.w, rect.h) ** 2 * 4) return best;
  return null;
}

type OpeningKind = 'rough' | 'window' | 'manDoor' | 'dock';

function classifyOpening(mark: string): OpeningKind {
  const m = mark.trim().toUpperCase();
  if (m.startsWith('RO')) return 'rough';
  if (m.startsWith('DL')) return 'dock';
  if (m.startsWith('W'))  return 'window';
  if (m.startsWith('D'))  return 'manDoor';
  return 'manDoor';
}

function placeOpening(panel: PanelData, r: Rect, mark: string, kind: OpeningKind, warnings: string[]): void {
  if (kind === 'rough') {
    const slot = nextSlot(panel.rovar);
    if (!slot) { warnings.push('Rough openings: out of slots — extra entry skipped.'); return; }
    slot.enabled = true;
    slot.data.x = r.x + r.w / 2;
    slot.data.y = r.y + r.h / 2;
    slot.data.width = r.w;
    slot.data.height = r.h;
    slot.data.mark = mark || slot.data.mark;
    return;
  }
  if (kind === 'window') {
    const slot = nextSlot(panel.wdvar);
    if (!slot) { warnings.push('Windows: out of slots — extra entry skipped.'); return; }
    slot.enabled = true;
    slot.data.x = r.x + r.w / 2;
    slot.data.y = r.y + r.h / 2;
    slot.data.width = r.w;
    slot.data.height = r.h;
    slot.data.sillHeight = r.y;
    slot.data.sill       = r.y;
    slot.data.headHeight = r.y + r.h;
    slot.data.mark = mark || slot.data.mark;
    return;
  }
  if (kind === 'dock') {
    const slot = nextSlot(panel.dlvar);
    if (!slot) { warnings.push('Dock levelers: out of slots — extra entry skipped.'); return; }
    slot.enabled = true;
    slot.data.x = r.x + r.w / 2;
    slot.data.y = r.y + r.h / 2;
    slot.data.width = r.w;
    slot.data.height = r.h;
    slot.data.mark = mark || slot.data.mark;
    return;
  }
  // manDoor — exporter draws rect at (x - w/2, y) with y = threshold (bottom of door)
  const slot = nextSlot(panel.drvar);
  if (!slot) { warnings.push('Man doors: out of slots — extra entry skipped.'); return; }
  slot.enabled = true;
  slot.data.x = r.x + r.w / 2;
  slot.data.y = r.y;        // bottom edge = threshold
  slot.data.width = r.w;
  slot.data.height = r.h;
  slot.data.mark = mark || slot.data.mark;
  if (!mark) {
    warnings.push(`Opening at (${r.x.toFixed(1)}, ${r.y.toFixed(1)}) had no text mark — defaulted to man door.`);
  }
}
